import { OpenLinkIcon } from 'components/icons/openlink'
import { WidgetsFormIcon } from 'components/icons/widgetsform'
import { Loading } from 'components/loading'
import { RasaContext } from 'context'
import { Dataset } from 'generic/dataset'
import React, { Component } from 'react'
import { newsletterWhitelistDomains, WhitelistDomains } from 'whitelist-domain/utils'
import * as GlobalConstants from '../../constants'
import * as GenericRedux from '../../generic/genericRedux'
import { copyToClipboard, getEnvironmentSuffix, isTruthy } from '../../generic/utility'
import { ENABLE_RECAPTCHA_KEY } from './constants'
import './styles.css'

declare const RASA_ONBOARDING_BASE_URL: string
interface FormLinkComponentProps {
  name: string,
  description: any,
  disabled?: boolean,
  link: string,
  inputLabel?: string,
  isRecommended?: boolean,
  hideLinkOpen?: boolean,
}

const FormLinkComponent = (props: FormLinkComponentProps) =>
  <div className="form-link">
    <div className="left-part">
      <div className="first-line">
        <div className="name">{props.name}</div>
        {props.isRecommended && <div className="recommended">RECOMMENDED</div>}
      </div>
      <div className="description" key={props.description}>
        {props.description.map((el, i) => {
          if (typeof el === 'string') {
            return el
          }
          return React.cloneElement(el, { key: i })
        })}
      </div>
    </div>
    <div className="middle-part">
      {!props.hideLinkOpen && <a target="_blank" href={props.link} className="open-link"><OpenLinkIcon /></a>}
    </div>
    <div className="right-part">
      <div className={`input ${props.disabled ? 'element-disabled' : ''}`}>
        <input type="text" value={props.link} readOnly={true} />
        <button onClick={() => copyToClipboard(props.link)}>Copy</button>
      </div>
      <div className="input-label">{props.inputLabel}</div>
    </div>
  </div>

interface FormEmbeddedComponentProps {
  name: string,
  description: string,
  text: string,
}

const FormEmbeddedComponent = (props: FormEmbeddedComponentProps) =>
  <div className="form-link form-embedded">
    <div className="left-part">
      <div className="name">{props.name}</div>
      <div className="description">{props.description}</div>
    </div>
    <div className="middle-part">
      <img className="link" src="" />
    </div>
    <div className="right-part">
      <div className="embedded-text-container">
        <textarea value={props.text} readOnly={true} />
        <button onClick={() => copyToClipboard(props.text)}>Copy</button>
      </div>
    </div>
  </div>

export interface WidgetsFormState {
  accountGuid: string,
  communityId: string,
  communityGuid: string,
  communityIdentifier: string,
  enableRecaptha: boolean,
  isLoading: boolean,
  newsletterDomains: WhitelistDomains,
}

class WidgetsFormComponent extends Component<any, WidgetsFormState> {
  public static contextType = RasaContext;

  constructor(props: any) {
    super(props)

    this.state = {
      accountGuid: '',
      communityId: '',
      communityGuid: '',
      communityIdentifier: '',
      enableRecaptha: false,
      isLoading: true,
      newsletterDomains: {},
    }
  }

  public componentDidMount = () => {
    this.context.user.init().then(({ person, activeCommunity }) => {
      this.setState({
        accountGuid: (person.accountInfo || {}).account_guid,
        communityId: activeCommunity.data.community_id,
        communityGuid: activeCommunity.data.community_guid,
        communityIdentifier: activeCommunity.communityId,
      }, () => {
        this.loadData()
      })

      newsletterWhitelistDomains((person.accountInfo || {}).account_guid, activeCommunity.communityId)
      .then((newsletterDomains: WhitelistDomains) => {
        this.setState({
          newsletterDomains,
          isLoading: false,
        })
      })
    })
  }

  public render() {
    const signupApiPalyload = `{
      <br>&ensp;"email": "john.doe@example.com",
      <br>&ensp;"first_name": "John",
      <br>&ensp;"last_name": "Doe"
      <br>}`
    return (
      <div>
        {this.state.isLoading ? <Loading size="64" /> :
          <div className="widgets-tools-forms">
            <div className="contentpane-header-wrapper">
              <div className="contentpane-icon-wrapper">
                <WidgetsFormIcon />
              </div>
              <div className="header">
                <div className="contentpane-section-title-wrapper">
                  <div className="section-header-text">Forms</div>
                </div>
                <div className="text">Great tools to help you boost your subscriber list</div>
              </div>
            </div>
            <FormLinkComponent
              name="Sign-up form"
              description={['Use this page to promote subscriptions to your newsletter. Anyone who opts in on this form will be added to your contacts.']}
              link={this.signupUrl()}
              isRecommended={false} />
            <FormEmbeddedComponent
              name="Sign-up Embed Widget"
              description="Have a website? Insert this code into the HTML
                to showcase your sign-up form anywhere on
                your site or blog."
              text={this.embeddedText()} />
            <FormLinkComponent
              name="Sign-up via API"
              description={[
                <div dangerouslySetInnerHTML={{__html: 'Use this API to add a contact to your newsletter. Send a <b>post</b> request to the api with <b>payload</b>:'}} />,
                <div dangerouslySetInnerHTML={{__html: signupApiPalyload}} />,
              ]}
              disabled={this.state.enableRecaptha}
              link={this.signupPublicAPIUrl()}
              hideLinkOpen={true}
              inputLabel={this.state.enableRecaptha ?
                'Direct API signup call will not work when Recaptcha is enabled.' : ''}
              isRecommended={false} />
            <FormLinkComponent
              name="Top Articles Page"
              description={['This is your personalized landing page that will dynamically update to showcase the most popular articles from your newsletter. Share this URL on other channels or ',
                <a href={GlobalConstants.RASA_HELP_EMBED_TOP_ARTICLE}
                  target="_blank"
                  rel="noopener noreferrer">learn how to embed it on your website here.</a>]}
              link={this.topArticleUrl()}
              isRecommended={false} />
            <FormLinkComponent
              name="Referral URL"
              description={['Use this page to promote rasa.io & get amazing discounts.']}
              link={this.referralUrl(this.state.accountGuid)}
              isRecommended={false} />
          </div>
        }
      </div>
    )
  }

  private topArticleUrl = () => {
    return `${this.pagesWebsite()}/articles/${this.state.communityGuid}`
  }
  private signupUrl = () => {
    return `${this.pagesWebsite()}/signup/${this.state.communityGuid}?utm_source=signup`
  }
  private referralUrl = (accountGuid) => {
    return `${RASA_ONBOARDING_BASE_URL}?referred_by=${accountGuid}`
  }
  private embeddedText = () => {
    return `<iframe width="560" height="315" src=${this.pagesWebsite()}/signup/${this.state.communityGuid}?utm_source=signup&iframe=true frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`
  }
  private signupPublicAPIUrl = () => {
    return `https://public-api${getEnvironmentSuffix()}.rasa.io/signup/${this.state.communityGuid}`
  }

  private pagesWebsite = (): string => {
    if (this.state.newsletterDomains.pages && this.state.newsletterDomains.pages.alternate_domain) {
      return `https://${this.state.newsletterDomains.pages.alternate_domain}`
    } else {
      return `https://pages${getEnvironmentSuffix()}.rasa.io`
    }
  }

  private loadData = () => {
    const params = [
      {param: 'key', value: ENABLE_RECAPTCHA_KEY},
    ]
    return new Dataset().loadCommunityDataset('communityConfiguration', this.state.communityIdentifier, params)
      .then((configuration) => {
        if (configuration.length > 0) {
          this.setState({
            enableRecaptha: configuration[0][0].value ? isTruthy(configuration[0][0].value) : false,
          })
        }
      })
  }
}

export const WidgetsForms = GenericRedux.registerNewDatasetComponent(
  WidgetsFormComponent, 'widgets_forms')
